import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  CheckCircle2, XCircle, Clock, MapPin, AlertTriangle,
  Eye, EyeOff, Calendar, CalendarDays, CalendarRange,
  FileText, ClipboardCheck, MessageSquare, Flag, X,
  Activity, Heart, Thermometer,
} from 'lucide-react';
import { toast } from 'sonner';

const LABEL_W = 160;
const ZOOM_DAY_W = { giorno: 38, settimana: 18, mese: 10 };

const statoConfig = {
  erogata: { bg: 'bg-current', border: 'border-current', opacity: '' },
  pianificata: { bg: 'bg-transparent', border: 'border-current border-dashed', opacity: 'opacity-60' },
  mancata: { bg: 'bg-red-500', border: 'border-red-500', opacity: '' },
};

/* ══════════ HOVER POPUP ══════════ */
function AccessoPopup({ accesso, colore, onClose }) {
  const isPianificata = accesso.stato === 'pianificata';
  const badgeLabel = isPianificata ? 'Pianificata' : accesso.stato === 'mancata' ? 'Mancata' : 'Erogata';
  const badgeBg = isPianificata ? `${colore}25` : `${colore}18`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
      className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-56"
      data-testid="accesso-popup"
    >
      <div className="bg-card border border-border rounded-xl shadow-lg p-3.5 text-left">
        <div className="flex items-center justify-between mb-2">
          <Badge className="text-[8px] border-0" style={{ backgroundColor: badgeBg, color: colore }}>
            {badgeLabel}
          </Badge>
          <button onClick={onClose} className="w-5 h-5 rounded flex items-center justify-center hover:bg-muted"><X className="w-3 h-3" /></button>
        </div>
        {accesso.nota && (
          <p className="text-[11px] font-semibold text-foreground">{accesso.nota}</p>
        )}
        {!isPianificata && accesso.parametri && (
          <p className="text-[10px] text-muted-foreground mt-1"><Activity className="w-3 h-3 inline mr-1" />{accesso.parametri}</p>
        )}
        {accesso.motivo && (
          <p className="text-[10px] text-red-500 mt-1"><AlertTriangle className="w-3 h-3 inline mr-1" />{accesso.motivo}</p>
        )}
        {accesso.geo && (
          <p className="text-[10px] text-muted-foreground mt-1"><MapPin className="w-3 h-3 inline mr-1" />{accesso.geo}</p>
        )}
      </div>
    </motion.div>
  );
}

/* ══════════ ALERT MODAL ══════════ */
function AlertModal({ alert, onClose, onConfirm }) {
  const [text, setText] = useState('');
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />
      <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="relative bg-card rounded-2xl border shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="gantt-alert-modal">
        <div className="p-5 border-b border-border">
          <h3 className="text-sm font-semibold flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-500" />Valore fuori soglia</h3>
          <p className="text-[11px] text-muted-foreground mt-1">{alert.label}: <strong>{alert.valore}{alert.unita}</strong> (soglia: {alert.sogliaStr})</p>
        </div>
        <div className="p-5">
          <label className="text-xs font-semibold text-foreground mb-1.5 block">Azione Intrapresa *</label>
          <textarea className="w-full h-24 rounded-xl border bg-muted/20 p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="Documenta l'azione clinica..." value={text} onChange={e => setText(e.target.value)} data-testid="gantt-alert-textarea" />
        </div>
        <div className="flex justify-end gap-3 p-5 border-t border-border">
          <Button variant="outline" size="sm" onClick={onClose}>Annulla</Button>
          <Button size="sm" className="gap-1.5 bg-red-600 hover:bg-red-700 text-white" disabled={!text.trim()} onClick={() => onConfirm(text)} data-testid="gantt-alert-confirm">
            <CheckCircle2 className="w-3.5 h-3.5" />Documenta
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ══════════ DAY HEADER ══════════ */
function DayHeader({ giorni, oggi, dayW, zoom }) {
  return (
    <div className="flex" style={{ minWidth: giorni * dayW }}>
      {Array.from({ length: giorni }, (_, i) => {
        const d = i + 1;
        const isToday = d === oggi;
        const showLabel = zoom === 'giorno'
          ? (d % 5 === 0 || d === 1)
          : (d % 7 === 1 || d === 1);
        return (
          <div key={d} className={`flex-shrink-0 text-center py-1.5 ${isToday ? 'bg-primary/8 rounded-t-lg' : ''}`} style={{ width: dayW }}>
            {showLabel ? (
              <span className={`text-[9px] font-semibold ${isToday ? 'text-primary' : 'text-muted-foreground'}`}>{d}</span>
            ) : (
              <span className="text-[9px] text-transparent">.</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ══════════ TODAY MARKER ══════════ */
function TodayLine({ oggi, dayW }) {
  const left = (oggi - 0.5) * dayW;
  return <div className="absolute top-0 bottom-0 w-px bg-primary/40 z-20 pointer-events-none" style={{ left }} />;
}

/* ══════════ LANE: PRESTAZIONI ══════════ */
function PrestazioniLane({ prof, giorni, oggi, dayW }) {
  const [popup, setPopup] = useState(null);
  return (
    <div className="flex items-center h-10 relative" style={{ minWidth: giorni * dayW }} data-testid={`lane-${prof.label.toLowerCase()}`}>
      <TodayLine oggi={oggi} dayW={dayW} />
      {prof.accessi.map((a, i) => {
        const left = (a.giorno - 1) * dayW + Math.max(dayW * 0.1, 2);
        const blockSize = Math.max(dayW * 0.8, 14);
        const isMancata = a.stato === 'mancata';
        const isErogata = a.stato === 'erogata';
        return (
          <div key={i} className="absolute" style={{ left, top: 4 }}>
            <div className="relative">
              <button
                className="rounded-lg border-2 flex items-center justify-center transition-transform hover:scale-110"
                style={{
                  width: blockSize,
                  height: blockSize,
                  borderColor: isMancata ? '#EF4444' : prof.colore,
                  backgroundColor: isErogata ? prof.colore : isMancata ? '#EF444420' : 'transparent',
                  borderStyle: a.stato === 'pianificata' ? 'dashed' : 'solid',
                }}
                onClick={() => setPopup(popup === i ? null : i)}
                data-testid={`block-${prof.label.toLowerCase()}-${a.giorno}`}
              >
                {isErogata && <CheckCircle2 className="text-white" style={{ width: blockSize * 0.45, height: blockSize * 0.45 }} />}
                {isMancata && <XCircle className="text-red-500" style={{ width: blockSize * 0.45, height: blockSize * 0.45 }} />}
                {a.stato === 'pianificata' && <Clock style={{ color: prof.colore, width: blockSize * 0.4, height: blockSize * 0.4 }} />}
              </button>
              <AnimatePresence>
                {popup === i && <AccessoPopup accesso={a} colore={prof.colore} onClose={() => setPopup(null)} />}
              </AnimatePresence>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ══════════ LANE: TELEMONITORAGGIO (SPARKLINE) ══════════ */
function TeleLane({ data, giorni, oggi, onAlertClick, dayW }) {
  const valori = data.valori;
  if (!valori.length) return <div className="h-10" style={{ minWidth: giorni * dayW }} />;

  const allVals = valori.map(v => v.valore);
  const min = Math.min(...allVals) - 2;
  const max = Math.max(...allVals) + 2;
  const range = max - min || 1;
  const H = 32;

  const isOutOfRange = (v) => {
    if (data.soglia.min !== undefined && v < data.soglia.min) return true;
    if (data.soglia.max !== undefined && v > data.soglia.max) return true;
    return false;
  };

  const sogliaStr = [
    data.soglia.min !== undefined ? `min ${data.soglia.min}` : '',
    data.soglia.max !== undefined ? `max ${data.soglia.max}` : '',
  ].filter(Boolean).join(', ');

  return (
    <div className="flex items-center h-10 relative" style={{ minWidth: giorni * dayW }} data-testid={`tele-${data.label.toLowerCase().replace(/\s|\./g, '')}`}>
      <TodayLine oggi={oggi} dayW={dayW} />
      <svg className="absolute inset-0 w-full h-full overflow-visible pointer-events-none" preserveAspectRatio="none">
        <polyline
          fill="none" stroke="#94A3B8" strokeWidth="1.5" strokeLinejoin="round"
          points={valori.map(v => `${(v.giorno - 0.5) * dayW},${H - ((v.valore - min) / range) * (H - 6) - 3}`).join(' ')}
        />
      </svg>
      {valori.map((v, i) => {
        const cx = (v.giorno - 0.5) * dayW;
        const cy = H - ((v.valore - min) / range) * (H - 6) - 3;
        const alert = isOutOfRange(v.valore);
        return (
          <button
            key={i}
            className={`absolute w-4 h-4 rounded-full border-2 flex items-center justify-center z-10 transition-transform hover:scale-125 ${alert ? 'bg-red-500 border-red-300' : 'bg-white border-slate-300 dark:bg-slate-800 dark:border-slate-600'}`}
            style={{ left: cx - 8, top: cy - 8 }}
            title={`${v.valore}${data.unita}`}
            onClick={() => alert && onAlertClick({ label: data.label, valore: v.valore, unita: data.unita, sogliaStr, giorno: v.giorno })}
            data-testid={`tele-point-${data.label.toLowerCase().replace(/\s|\./g, '')}-${v.giorno}`}
          >
            {alert && <span className="text-[7px] font-bold text-white">!</span>}
          </button>
        );
      })}
    </div>
  );
}

/* ══════════ LANE: MILESTONES & VERSIONING ══════════ */
function MilestoneLane({ paiSteps, versioning, scadenzaPAI, rivalutazione, giorni, oggi, dayW }) {
  const parse = (s) => { const p = s.split('/'); return parseInt(p[0]); };
  return (
    <div className="flex items-center h-10 relative" style={{ minWidth: giorni * dayW }} data-testid="lane-milestones">
      <TodayLine oggi={oggi} dayW={dayW} />
      {paiSteps.filter(s => s.data).map((s, i) => {
        const day = parse(s.data);
        const left = (day - 0.5) * dayW;
        const done = s.stato === 'completato';
        return (
          <div key={i} className="absolute flex flex-col items-center" style={{ left: left - 10, top: 2 }}>
            <div className={`w-5 h-5 rounded-full flex items-center justify-center ${done ? 'bg-emerald-100' : 'bg-violet-100'}`}>
              {done ? <CheckCircle2 className="w-3 h-3 text-emerald-500" /> : <Flag className="w-3 h-3 text-violet-500" />}
            </div>
            <span className="text-[7px] text-muted-foreground mt-0.5 whitespace-nowrap">{s.label.split(' ').map(w => w[0]).join('')}</span>
          </div>
        );
      })}
      {versioning.map((v, i) => (
        <div key={`v${i}`} className="absolute top-0 bottom-0 border-l-2 border-dashed border-indigo-300 z-10" style={{ left: (v.giorno - 0.5) * dayW }}>
          <Badge className="absolute -top-0.5 left-1 text-[7px] bg-indigo-50 text-indigo-600 border-0 whitespace-nowrap">{v.versione}</Badge>
        </div>
      ))}
      <div className="absolute top-0 bottom-0 border-l-2 border-red-300 z-10" style={{ left: (scadenzaPAI - 0.5) * dayW }}>
        <Badge className="absolute -top-0.5 left-1 text-[7px] bg-red-50 text-red-500 border-0 whitespace-nowrap">Scad.</Badge>
      </div>
      <div className="absolute top-0 bottom-0 border-l-2 border-amber-300 z-10" style={{ left: (rivalutazione - 0.5) * dayW }}>
        <Badge className="absolute -top-0.5 left-1 text-[7px] bg-amber-50 text-amber-600 border-0 whitespace-nowrap">Rival.</Badge>
      </div>
    </div>
  );
}

/* ══════════ LANE: OUTCOMES ══════════ */
function OutcomesLane({ outcomes, giorni, oggi, dayW }) {
  const tipoIcon = { caregiver: ClipboardCheck, proms: FileText, prems: MessageSquare };
  const tipoColor = { caregiver: '#10B981', proms: '#3B82F6', prems: '#8B5CF6' };
  return (
    <div className="flex items-center h-10 relative" style={{ minWidth: giorni * dayW }} data-testid="lane-outcomes">
      <TodayLine oggi={oggi} dayW={dayW} />
      {outcomes.map((o, i) => {
        const left = (o.giorno - 0.5) * dayW;
        const Icon = tipoIcon[o.tipo] || FileText;
        const color = tipoColor[o.tipo] || '#64748B';
        return (
          <div key={i} className="absolute flex flex-col items-center" style={{ left: left - 10, top: 4 }} title={o.label} data-testid={`outcome-${o.giorno}`}>
            <div className={`w-6 h-6 rounded-lg flex items-center justify-center ${o.completato ? '' : 'opacity-40'}`} style={{ backgroundColor: `${color}18` }}>
              <Icon className="w-3.5 h-3.5" style={{ color }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ══════════ LABEL COLONNA SINISTRA ══════════ */
function LaneLabel({ label, icon: Icon, color, sub }) {
  return (
    <div className="flex items-center gap-2 h-10 px-3 border-r border-border/30 flex-shrink-0" style={{ width: LABEL_W }}>
      <div className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}14` }}>
        <Icon className="w-3.5 h-3.5" style={{ color }} />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] font-semibold text-foreground truncate">{label}</p>
        {sub && <p className="text-[8px] text-muted-foreground truncate">{sub}</p>}
      </div>
    </div>
  );
}

function GroupHeader({ label }) {
  return (
    <div className="flex items-center h-7 bg-muted/20 border-y border-border/20">
      <div className="px-3 flex-shrink-0" style={{ width: LABEL_W }}>
        <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider">{label}</span>
      </div>
    </div>
  );
}

/* ══════════ MAIN GANTT COMPONENT ══════════ */
export default function ClinicalGantt({ data, paiSteps }) {
  const scrollRef = useRef(null);
  const [zoom, setZoom] = useState('giorno');
  const [hiddenLanes, setHiddenLanes] = useState({});
  const [alertModal, setAlertModal] = useState(null);

  if (!data) return null;

  const { giorni, oggi, prestazioni, telemonitoraggio, versioning, scadenzaPAI, rivalutazione, outcomes, meseLabel } = data;
  const dayW = ZOOM_DAY_W[zoom] || 38;

  const toggleLane = (key) => setHiddenLanes(prev => ({ ...prev, [key]: !prev[key] }));

  const profKeys = Object.keys(prestazioni);
  const teleKeys = Object.keys(telemonitoraggio);

  const handleAlertConfirm = (text) => {
    toast.success('Azione documentata', { description: `Alert ${alertModal.label} registrato nel diario clinico.` });
    setAlertModal(null);
  };

  return (
    <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl overflow-hidden" data-testid="clinical-gantt">
      <CardContent className="p-0">
        {/* Toolbar */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-border/30" data-testid="gantt-toolbar">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-indigo-50 flex items-center justify-center">
              <CalendarRange className="w-4 h-4 text-indigo-500" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-foreground">Gantt Clinico-Logistico</h2>
              <p className="text-[10px] text-muted-foreground">{meseLabel} · Giorno corrente: {oggi}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {[
              { key: 'giorno', icon: Calendar, label: 'G' },
              { key: 'settimana', icon: CalendarDays, label: 'S' },
              { key: 'mese', icon: CalendarRange, label: 'M' },
            ].map(z => (
              <button
                key={z.key}
                onClick={() => setZoom(z.key)}
                className={`w-8 h-8 rounded-lg flex items-center justify-center text-[10px] font-bold transition-colors ${zoom === z.key ? 'bg-primary/10 text-primary ring-1 ring-primary/30' : 'bg-muted/30 text-muted-foreground hover:bg-muted/50'}`}
                data-testid={`zoom-${z.key}`}
              >
                {z.label}
              </button>
            ))}
            <div className="w-px h-6 bg-border/40 mx-1" />
            {[
              { key: 'prestazioni', label: 'Prestazioni' },
              { key: 'telemonitoraggio', label: 'Telemon.' },
              { key: 'outcomes', label: 'Outcomes' },
            ].map(f => (
              <button
                key={f.key}
                onClick={() => toggleLane(f.key)}
                className={`px-2.5 h-7 rounded-lg text-[10px] font-semibold flex items-center gap-1 transition-colors ${hiddenLanes[f.key] ? 'bg-muted/20 text-muted-foreground line-through' : 'bg-muted/40 text-foreground'}`}
                data-testid={`filter-${f.key}`}
              >
                {hiddenLanes[f.key] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Gantt Body */}
        <div className="flex">
          {/* Fixed labels column */}
          <div className="flex-shrink-0 border-r border-border/20" style={{ width: LABEL_W }}>
            <div className="h-8 border-b border-border/20" />
            <GroupHeader label="Percorso PAI" />
            <LaneLabel label="Milestones" icon={Flag} color="#6366F1" sub="Step & versioni" />
            {!hiddenLanes.prestazioni && (
              <>
                <GroupHeader label="Prestazioni" />
                {profKeys.map(k => (
                  <LaneLabel key={k} label={prestazioni[k].label} icon={Heart} color={prestazioni[k].colore} sub={`${prestazioni[k].accessi.length} accessi`} />
                ))}
              </>
            )}
            {!hiddenLanes.telemonitoraggio && (
              <>
                <GroupHeader label="Telemonitoraggio" />
                {teleKeys.map(k => (
                  <LaneLabel key={k} label={telemonitoraggio[k].label} icon={Thermometer} color="#64748B" sub={telemonitoraggio[k].unita} />
                ))}
              </>
            )}
            {!hiddenLanes.outcomes && (
              <>
                <GroupHeader label="Outcomes" />
                <LaneLabel label="Paziente" icon={ClipboardCheck} color="#10B981" sub="PROMs / Caregiver" />
              </>
            )}
          </div>

          {/* Scrollable Gantt area */}
          <div className="flex-1 overflow-x-auto" ref={scrollRef} data-testid="gantt-scroll-area">
            <div style={{ minWidth: giorni * dayW }}>
              <div className="border-b border-border/20">
                <DayHeader giorni={giorni} oggi={oggi} dayW={dayW} zoom={zoom} />
              </div>
              <GroupHeader label="" />
              <div className="border-b border-border/10">
                <MilestoneLane paiSteps={paiSteps} versioning={versioning} scadenzaPAI={scadenzaPAI} rivalutazione={rivalutazione} giorni={giorni} oggi={oggi} dayW={dayW} />
              </div>
              {!hiddenLanes.prestazioni && (
                <>
                  <GroupHeader label="" />
                  {profKeys.map(k => (
                    <div key={k} className="border-b border-border/10">
                      <PrestazioniLane prof={prestazioni[k]} giorni={giorni} oggi={oggi} dayW={dayW} />
                    </div>
                  ))}
                </>
              )}
              {!hiddenLanes.telemonitoraggio && (
                <>
                  <GroupHeader label="" />
                  {teleKeys.map(k => (
                    <div key={k} className="border-b border-border/10">
                      <TeleLane data={telemonitoraggio[k]} giorni={giorni} oggi={oggi} onAlertClick={setAlertModal} dayW={dayW} />
                    </div>
                  ))}
                </>
              )}
              {!hiddenLanes.outcomes && (
                <>
                  <GroupHeader label="" />
                  <div className="border-b border-border/10">
                    <OutcomesLane outcomes={outcomes} giorni={giorni} oggi={oggi} dayW={dayW} />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 px-5 py-3 border-t border-border/20 bg-muted/10">
          {[
            { el: <div className="w-4 h-4 rounded border-2 border-cyan-500 bg-cyan-500 flex items-center justify-center"><CheckCircle2 className="w-2.5 h-2.5 text-white" /></div>, label: 'Erogata' },
            { el: <div className="w-4 h-4 rounded border-2 border-dashed border-cyan-500" />, label: 'Pianificata' },
            { el: <div className="w-4 h-4 rounded border-2 border-red-400 bg-red-50 flex items-center justify-center"><XCircle className="w-2.5 h-2.5 text-red-500" /></div>, label: 'Mancata' },
            { el: <div className="w-3 h-3 rounded-full bg-red-500" />, label: 'Fuori soglia' },
            { el: <div className="w-4 border-t-2 border-dashed border-indigo-300" />, label: 'Versione PAI' },
            { el: <div className="w-4 border-t-2 border-red-300" />, label: 'Scadenza PAI' },
            { el: <div className="w-4 border-t-2 border-amber-300" />, label: 'Rivalutazione' },
          ].map((l, i) => (
            <div key={i} className="flex items-center gap-1.5">
              {l.el}
              <span className="text-[9px] text-muted-foreground font-medium">{l.label}</span>
            </div>
          ))}
        </div>
      </CardContent>

      <AnimatePresence>
        {alertModal && <AlertModal alert={alertModal} onClose={() => setAlertModal(null)} onConfirm={handleAlertConfirm} />}
      </AnimatePresence>
    </Card>
  );
}
